/**
 * avisos.ts · Dado tipado do Painel de Avisos da tela de login do Órbita.
 *
 * Separa o conteúdo (dados) da renderização (LoginPage METADE B), no padrão
 * "engine puro": nenhum texto de aviso vive no JSX. Editoria executiva definida
 * em `Contexto/arquitetura/AVISOS-EDITORIA.md`. Regra de escrita: português
 * executivo, sóbrio, sem travessão e sem placeholder em produção.
 *
 * Quando os avisos passarem a vir de fonte remota (Firestore/config), esta
 * constante vira o resultado de um fetch; o tipo `Aviso` permanece o contrato.
 */

// Categoria: a "trilha" a que o aviso pertence (define a seção do painel).
export type AvisoCategoria = "comercial" | "plataforma" | "novidade";

// Severidade: o peso visual e a urgência. Mapeia para cor semântica (ver
// LoginPage → cor de borda/badge por severidade, tokens de status dark).
//   info     -> ciano   (informativo, sem ação urgente)
//   sucesso  -> teal    (algo liberado / disponível agora)
//   atencao  -> âmbar   (exige planejamento ou ação do cliente)
//   critico  -> vermelho (impacto operacional se ignorado; usar com parcimônia)
export type AvisoSeveridade = "info" | "sucesso" | "atencao" | "critico";

// Tipo de ação (CTA):
//   link    -> leva a uma página/recurso.
//   contato -> aciona contato com o time (comercial/integração).
//   info    -> ação que acontece dentro do produto após o login.
export type AvisoAcaoTipo = "link" | "contato" | "info";

export interface AvisoAcao {
  rotulo: string; // texto do CTA (verbo + objeto, sem ponto final)
  tipo: AvisoAcaoTipo;
}

export interface Aviso {
  id: string; // slug estável (ex.: "football-field-valuation")
  categoria: AvisoCategoria;
  severidade: AvisoSeveridade;
  titulo: string; // executivo, curto
  resumo: string; // o que aconteceu + número/impacto (prazo, %, valor)
  acao: AvisoAcao | null; // null quando não há ação do usuário
  data?: string; // YYYY-MM-DD: quando foi publicado (ordena novidades)
  prazo?: string; // YYYY-MM-DD: data-limite relevante (lancamento, janela)
  tag?: string; // rótulo curto opcional (ex.: "Beta", "Migração")
}

/**
 * Avisos ativos. Ordem já reflete a regra de exibição do doc de editoria
 * (plataforma > novidade > comercial); o componente reagrupa e reordena por
 * categoria/severidade/prazo. O item de "Manutenção programada" fica de fora
 * até haver data real: painel honesto não mostra placeholder XX/XX.
 */
export const AVISOS: Aviso[] = [
  // --- PLATAFORMA ---
  {
    id: "sankhya-api-auth",
    categoria: "plataforma",
    severidade: "atencao",
    titulo: "Manutenção programada",
    resumo:
      "Janela de manutenção para a migração do Sankhya entre os dias 28 e 30 de setembro. Durante a janela, a integração com o Sankhya pode ficar indisponível. Planeje-se.",
    acao: { rotulo: "Falar com o time de integração", tipo: "contato" },
    prazo: "2026-09-30",
    tag: "Manutenção",
  },

  // --- NOVIDADE (o que há de novo) ---
  {
    id: "analise-dupont",
    categoria: "novidade",
    severidade: "sucesso",
    titulo: "Análise DuPont liberada",
    resumo:
      "Veja de forma visual o que puxa o retorno da empresa: margem, giro ou alavancagem. Disponível agora.",
    acao: { rotulo: "Ver com meus dados", tipo: "contato" },
    tag: "Novo",
  },
  {
    id: "alertas-inteligentes",
    categoria: "novidade",
    severidade: "sucesso",
    titulo: "Alertas por condição",
    resumo:
      "Defina a regra (ex.: caixa projetado abaixo de R$ 1 mi em 30 dias) e o Órbita monitora sozinho.",
    acao: { rotulo: "Criar um alerta", tipo: "info" },
    tag: "Novo",
  },
  {
    id: "rastreabilidade-dado",
    categoria: "novidade",
    severidade: "sucesso",
    titulo: "Origem e hora de cada dado",
    resumo:
      "Cada número agora mostra a fonte, o horário da última sincronização e quando foi atualizado.",
    acao: null,
    tag: "Disponível",
  },

  // --- COMERCIAL (disponível em breve) ---
  {
    id: "football-field-valuation",
    categoria: "comercial",
    severidade: "atencao",
    titulo: "Valuation em beta",
    resumo:
      "Modelos de valuation entram em produção até 30/09. Quem entra no beta tem 50% de desconto no primeiro ano.",
    acao: { rotulo: "Manifestar interesse", tipo: "contato" },
    prazo: "2026-09-30",
    tag: "Beta · 50% off",
  },
  {
    id: "alertas-whatsapp",
    categoria: "comercial",
    severidade: "info",
    titulo: "Alertas por WhatsApp",
    resumo:
      "Indicadores, alertas e cobrança de plano de ação chegam pelo WhatsApp. Entra em produção até 18/11.",
    acao: { rotulo: "Quero na minha operação", tipo: "contato" },
    prazo: "2026-11-18",
    tag: "Em breve",
  },
];
